// filepath: client/src/components/accessibility/AccessibilityEnhancer.tsx
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface AccessibilityEnhancerProps {
  children: React.ReactNode;
}

/**
 * ARIA live announcements only.
 * document.title is owned exclusively by SEOHead — do not set it here.
 */
const ROUTE_ANNOUNCEMENTS: Record<string, string> = {
  '/': 'Navigated to FlahaFAST home page. Hydroponic nutrient decision support.',
  '/support': 'Navigated to support page. Documentation and help resources available.',
  '/support/video-tutorials': 'Navigated to video tutorials.',
  '/support/agronomic-principles': 'Navigated to agronomic principles guide.',
  '/support/water-quality-management': 'Navigated to water quality management guide.',
  '/support/advanced-calculations': 'Navigated to advanced calculations guide.',
  '/support/yield-optimization': 'Navigated to yield optimization guide.',
  '/login': 'Navigated to login page. Enter credentials to access your account.',
  '/register': 'Navigated to registration page.',
  '/dashboard': 'Navigated to workspace. Pipeline overview and resume actions available.',
  '/water-quality': 'Navigated to water quality. Input water parameters for calculation.',
  '/water-profile': 'Navigated to water quality. Input water parameters for calculation.',
  '/formulation': 'Navigated to crop formulation. Select target crop and growth stage.',
  '/salt-library': 'Navigated to nutrient salt library. Choose compatible salts for your solution.',
  '/stock-solutions': 'Navigated to recipe stage. Configure stock solutions and solve.',
  '/calculation': 'Navigated to calculator.',
  '/results': 'Navigated to calculation results. Review and download your nutrient recipe.',
  '/profile': 'Navigated to user profile.',
  '/admin': 'Navigated to admin dashboard.',
};

const AccessibilityEnhancer: React.FC<AccessibilityEnhancerProps> = ({ children }) => {
  const location = useLocation();

  useEffect(() => {
    // Do NOT set document.title — SEOHead is the single owner

    const announcement =
      ROUTE_ANNOUNCEMENTS[location.pathname] ||
      (location.pathname.startsWith('/support')
        ? 'Navigated to support documentation.'
        : location.pathname.startsWith('/verify-email')
          ? 'Navigated to email verification.'
          : undefined);
    if (announcement) {
      announceToScreenReader(announcement);
    }

    // Focus main when present (Layout / public shells may own #main-content)
    const mainContent =
      document.getElementById('main-content') ||
      document.querySelector('main') ||
      document.querySelector('[role="main"]');
    if (mainContent) {
      (mainContent as HTMLElement).focus();
    }

    enhanceKeyboardNavigation();
    detectHighContrastMode();
    handleReducedMotionPreference();
  }, [location.pathname]);

  return (
    <>
      <div className="sr-only" aria-live="polite" id="route-announcer" />

      <a
        href="#main-content"
        className="skip-to-content"
        style={{
          position: 'absolute',
          top: '-40px',
          left: '6px',
          background: '#057c69',
          color: 'white',
          padding: '8px',
          textDecoration: 'none',
          borderRadius: '4px',
          zIndex: 9999,
          transition: 'top 0.3s',
        }}
        onFocus={(e) => {
          (e.target as HTMLElement).style.top = '6px';
        }}
        onBlur={(e) => {
          (e.target as HTMLElement).style.top = '-40px';
        }}
      >
        Skip to main content
      </a>

      {/* Fragment only — Layout/public shells own <main id="main-content"> */}
      {children}
    </>
  );
};

// Helper functions
const announceToScreenReader = (message: string) => {
  const announcer = document.getElementById('route-announcer');
  if (announcer) {
    announcer.textContent = message;
    // Clear after announcement
    setTimeout(() => {
      announcer.textContent = '';
    }, 1000);
  }
};

const enhanceKeyboardNavigation = () => {
  // Avoid stacking listeners on every route change
  if ((window as unknown as { __flahaKbNav?: boolean }).__flahaKbNav) {
    return;
  }
  (window as unknown as { __flahaKbNav?: boolean }).__flahaKbNav = true;

  document.addEventListener('keydown', (e) => {
    // Escape key to close modals/dropdowns
    if (e.key === 'Escape') {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && activeElement.blur) {
        activeElement.blur();
      }
    }

    // Alt + M to focus main content
    if (e.altKey && e.key === 'm') {
      e.preventDefault();
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.focus();
      }
    }

    // Alt + N to focus navigation
    if (e.altKey && e.key === 'n') {
      e.preventDefault();
      const navigation = document.querySelector('nav') || document.querySelector('[role="navigation"]');
      if (navigation) {
        (navigation as HTMLElement).focus();
      }
    }
  });
};

const detectHighContrastMode = () => {
  // Detect Windows High Contrast Mode
  if (window.matchMedia('(prefers-contrast: high)').matches) {
    document.body.classList.add('high-contrast');
  }

  // Listen for changes
  window.matchMedia('(prefers-contrast: high)').addEventListener('change', (e) => {
    if (e.matches) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
  });
};

const handleReducedMotionPreference = () => {
  // Respect user's motion preferences
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    document.body.classList.add('reduced-motion');
    
    // Add CSS for reduced motion
    const style = document.createElement('style');
    style.textContent = `
      .reduced-motion *,
      .reduced-motion *::before,
      .reduced-motion *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Listen for changes
  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
    if (e.matches) {
      document.body.classList.add('reduced-motion');
    } else {
      document.body.classList.remove('reduced-motion');
    }
  });
};

export default AccessibilityEnhancer;
